import { Component, OnDestroy, Input } from "@angular/core";
import { ModalController, NavParams, ToastController, LoadingController } from "@ionic/angular";
import { BaseClass } from "src/app/shared/classes/base";
import { TranslationService } from "src/app/shared/services/translation.service";
import { BehaviorSubject } from "rxjs";
import { debounceTime, distinctUntilChanged, filter } from "rxjs/operators";
import { UserItem } from "src/app/shared/services/auth.service";
import { GroupsService } from "src/app/groups/groups.service";
import { UserRule } from "../../_interfaces/user-visibility";
import { PerformanceCycle } from "src/app/performance-cycles/_interfaces/performance-cycle.interface";
import { PerformanceCycleService } from "src/app/performance-cycles/performance-cycles.service";
import { UsersService } from "../../users.service";
import { ImportVisibilityParticipantsComponent } from "../import-visibility-participants/import-visibility-participants.component";

@Component({
    selector: "app-add-rule-modal",
    templateUrl: "./add-rule.modal.html",
    styleUrls: ["./add-rule.modal.scss"],
})
export class AddVisibilityRuleModal extends BaseClass implements OnDestroy {
    @Input() userId: string;
    public selectedCycle: number;
    public cycles: PerformanceCycle[] = [];
    private searchSubject: BehaviorSubject<string> = new BehaviorSubject("");
    public users: any[] = [];
    @Input() public userRules: UserRule[] = [];

    constructor(
        protected _toastController: ToastController,
        private _loadingController: LoadingController,
        protected _translationService: TranslationService,
        private _modalController: ModalController,
        private _navParams: NavParams,
        private _groupsService: GroupsService,
        private _cycleService: PerformanceCycleService,
        private _userService: UsersService,
    ) {
        super(_toastController, _translationService);
        this._loadCycles();
        this.subscribeSearch();
    }

    public dismiss(data?: UserRule[]): void {
        this._modalController.dismiss(data);
    }

    public async save() {
        const loading = await this._loadingController.create();
        await loading.present();

        this._userService.updateIndividualVisibilityRules(this.userId, this.userRules).subscribe(
            (response) => {
                loading.dismiss();
                this.dismiss(this.userRules);
            },
            (error) => {
                loading.dismiss();
                this.notifyError(error);
            },
        );
    }

    public async addGroup(): Promise<void> {
        const modal = await this._modalController.create({
            component: ImportVisibilityParticipantsComponent,
            componentProps: {
                cycleId: this.selectedCycle,
            },
        });

        modal.onDidDismiss().then((event) => {
            if (event && event.data) {
                if (event.data[1]) {
                    this.userRules = [];
                }
                (<any[]>event.data[0]).forEach((x) => this.addUser(x));
            }
        });
        return await modal.present();
    }

    public onChangeSearch(event) {
        this.searchSubject.next(event.srcElement.value);
    }

    public subscribeSearch() {
        this.searchSubject.pipe(distinctUntilChanged(), debounceTime(1000)).subscribe((data) => {
            if (data) {
                this._loadUsers(data);
            } else {
                this.users = [];
            }
        });
    }

    private async _loadCycles() {
        const response = await this._cycleService.getLastCycles().toPromise();
        this.cycles = response.data;
        if (this.cycles && this.cycles.length > 0) this.selectedCycle = this.cycles[0].id;
    }

    private _loadUsers(searchValue: string = ""): void {
        this._groupsService.getUsersToShareCalibrationGroup(this.selectedCycle, searchValue, [], 1, 5).subscribe(
            (response) => {
                this.users = response.data.users;
            },
            (error) => this.notifyError(error),
        );
    }

    public selectUser(index: number) {
        const user = this.users[index];
        this.addUser(user);
    }

    private addUser(user: any) {
        const cycle = this.cycles.find((x) => x.id === this.selectedCycle);

        if (this.userRules.find((x) => x.cycleParticipantId === user.cycleParticipantId)) return;

        const rule = <UserRule>{
            cycleName: cycle.name,
            imageUrl: user.imageUrl,
            name: user.name,
            businessGroup: user.attribute1,
            rank: user.attribute2,
            userId: user.userId,
            cycleParticipantId: user.cycleParticipantId,
            cycleId: cycle.id,
        };
        this.userRules.push(rule);
    }

    public removeUser(index: number): void {
        this.userRules.splice(index, 1);
    }

    ngOnDestroy(): void {
        this.searchSubject.unsubscribe();
    }
}
